/**
 * 동의 (consent) — 클라이언트 측 read/write 헬퍼 (Phase 0).
 *
 * 저장:
 *   - localStorage: bs:consent:v1 = { necessary, analytics, marketing, ts, version }
 *   - 로그인 사용자: 추가로 POST /api/consent 로 서버 동기화
 *
 * 호출 흐름:
 *   1. 첫 방문 → loadLocalConsent() = null → ConsentBanner 표시
 *   2. "모두 동의" / "필수만 동의" / "세부 설정" 선택 → saveLocalConsent + (로그인이면) syncToServer
 *   3. analytics 게이트 → hasAnalyticsConsent() 체크 후 send
 *
 * 약관 버전 변경:
 *   - 서버가 GET /api/consent/version 으로 현재 version 반환
 *   - localStorage 의 version 보다 높으면 → 재동의 트리거 (배너 다시 표시)
 */

import { clearBiteAnalyticsEntry } from './bite-entry';

const STORAGE_KEY = "bs:consent:v1";

export interface ConsentState {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  ts: number; // epoch ms — 동의 시점
  version: number;
}

export function defaultConsent(): ConsentState {
  return {
    necessary: true,
    analytics: false,
    marketing: false,
    ts: 0,
    version: 1,
  };
}

/**
 * localStorage 에서 동의 상태 읽기. 없으면 null (= 한 번도 동의한 적 없음 — 배너 표시 필요).
 */
export function loadLocalConsent(): ConsentState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      necessary: true,
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
      ts: Number(parsed.ts) || 0,
      version: Number(parsed.version) || 1,
    };
  } catch {
    return null;
  }
}

/**
 * localStorage 에 동의 상태 저장 (로그인/비로그인 공통).
 */
export function saveLocalConsent(updates: Partial<Omit<ConsentState, "necessary">>): ConsentState {
  if (updates.analytics === false) clearBiteAnalyticsEntry();
  const prev = loadLocalConsent() ?? defaultConsent();
  const next: ConsentState = {
    necessary: true,
    analytics: updates.analytics ?? prev.analytics,
    marketing: updates.marketing ?? prev.marketing,
    ts: Date.now(),
    version: updates.version ?? Math.max(prev.version, requiredVersion),
  };
  if (typeof window !== "undefined") {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  }
  if(typeof window!=="undefined"&&typeof Event!=="undefined")window.dispatchEvent?.(new Event("bite-consent-change"));
  return next;
}

/**
 * 로그인 사용자: 서버에 동의 상태 동기화 (POST /api/consent).
 * 비로그인은 서버가 401 반환 — 호출자가 무시.
 */
async function postConsentToServer(updates: Partial<Pick<ConsentState, "analytics" | "marketing">>): Promise<boolean> {
  if (typeof window === "undefined") return false;
  try {
    const { CSRF_HEADER, getCsrfToken } = await import("@/lib/csrf");
    const csrf = await getCsrfToken().catch(() => "");
    const res = await fetch("/api/consent", {
      method: "POST",
      credentials: "include",
      signal: AbortSignal.timeout(5000),
      headers: {
        "Content-Type": "application/json",
        ...(csrf ? { [CSRF_HEADER]: csrf } : {}),
      },
      body: JSON.stringify(updates),
    });
    return res.ok;
  } catch {
    return false;
  }
}

let syncQueue = Promise.resolve(true);
export function waitForConsentSync() { return syncQueue; }
export function syncConsentToServer(updates: Partial<Pick<ConsentState, "analytics" | "marketing">>) {
  syncQueue = syncQueue.then(() => postConsentToServer(updates));
  return syncQueue;
}

/**
 * 서버 → 클라이언트 동의 상태 fetch (로그인 후 첫 mount 시 sync).
 * 로그인 안 됐으면 default 반환.
 */
export async function fetchServerConsent(): Promise<{ email: string | null; consent: ConsentState; version: number }> {
  if (typeof window === "undefined") {
    return { email: null, consent: defaultConsent(), version: 1 };
  }
  try {
    const res = await fetch("/api/consent", { credentials: "include", cache: "no-store", signal: AbortSignal.timeout(5000) });
    if (!res.ok) return { email: null, consent: defaultConsent(), version: 1 };
    const json = await res.json();
    return {
      email: json.email ?? null,
      consent: {
        necessary: true,
        analytics: Boolean(json.consent?.analytics),
        marketing: Boolean(json.consent?.marketing),
        ts: Number(json.consent?.updatedAt) || 0,
        version: Number(json.consent?.version) || 1,
      },
      version: Number(json.version) || 1,
    };
  } catch {
    return { email: null, consent: defaultConsent(), version: 1 };
  }
}

/**
 * 분석 추적 동의 여부 — analytics SDK 의 게이트.
 * 동의한 적 없으면 (null) → false (보수적).
 */
export function hasAnalyticsConsent(): boolean {
  const c = loadLocalConsent();
  return c !== null && c.version >= requiredVersion && c.analytics === true;
}

export function hasMarketingConsent(): boolean {
  const c = loadLocalConsent();
  return c !== null && c.marketing === true;
}

/**
 * 동의 한 번도 안 한 상태 — ConsentBanner 표시 트리거.
 */
export function needsConsent(currentVersion = 1): boolean {
  const c = loadLocalConsent();
  if (!c) return true;
  if (c.version < currentVersion) return true;
  return false;
}

function rememberConsent(consent: ConsentState) {
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(consent)); } catch { /* storage unavailable */ }
  if(typeof Event!=="undefined")window.dispatchEvent?.(new Event("bite-consent-change"));
  return consent;
}

let initialization: Promise<{ consent: ConsentState; needsBanner: boolean }> | null = null;
let requiredVersion = 1;

/** An empty account record is not a rejection of the browser's actual choice. */
export function initializeConsent() {
  if (initialization) return initialization;
  initialization = (async () => {
    const local = loadLocalConsent();
    const server = await fetchServerConsent();
    requiredVersion = server.version;
    const latest = loadLocalConsent();
    if (latest?.ts !== local?.ts) return { consent: latest ?? defaultConsent(), needsBanner: !latest?.ts };
    if (server.email && server.consent.ts > 0) {
      rememberConsent(server.consent);
      const expired = server.consent.version < server.version;
      return { consent: expired ? defaultConsent() : server.consent, needsBanner: expired };
    }
    if (local && local.ts > 0 && local.version >= server.version) {
      if (server.email) await syncConsentToServer({ analytics: local.analytics, marketing: local.marketing });
      return { consent: loadLocalConsent() ?? local, needsBanner: false };
    }
    return { consent: defaultConsent(), needsBanner: true };
  })();
  return initialization;
}
